// publication.js
import PublicationError from "%/dynamic-component-editor/errors/publication-error";
import { links } from "%/dynamic-component-editor/enums/link-enums";
import BaseSingletonEntity from "%/dynamic-component-editor/classes/base-singleton-entity";

// Lazy store resolution: a module-scope import here closes a cycle
// (stores -> components -> mixins -> classes). See scripts/find-import-cycles.py.
// eslint-disable-next-line global-require
const useConfigurationStore = (...args) => require("%/dynamic-component-editor/stores/configuration-store").useConfigurationStore(...args);

class Publication extends BaseSingletonEntity {
  /** ******************* Private Static Vars *********************/
  static #allowConstruction = false;

  /** ******************* Private Vars *********************/
  #store = null;
  #id = null;
  #designVariant = "desktop";

  /** ******************* Public Static Methods *********************/
  static get(id) {
    const instanceKey = `publication-${id}`;
    return BaseSingletonEntity._getInstance(Publication, instanceKey, () => {
      Publication.#allowConstruction = true;
      const instance = new Publication(id);
      Publication.#allowConstruction = false;
      return instance;
    });
  }

  /** ******************* Public Getters *********************/
  get id() {
    return this.#id;
  }

  get version() {
    return this.#store.currentVersion;
  }

  get savingMessage() {
    return this.#store.savingMessage;
  }

  get links() {
    return this.#store.links;
  }

  get apiV1() {
    return this.links[links.api];
  }

  get designVariant() {
    return this.#store.designVariant;
  }

  /** ******************* Public Setters *********************/
  set savingMessage(status) {
    this.#store.savingMessage = status;
  }

  set version(versionId) {
    this.#store.currentVersion = versionId;
  }

  set designVariant(variant) {
    useConfigurationStore().updateConfiguration({
      designVariant: variant,
    });
    this.#designVariant = variant;
    this.#store.designVariant = variant;
  }

  /** ******************* Public Methods *********************/
  constructor(id) {
    super();
    if (!Publication.#allowConstruction) {
      throw new PublicationError("Use Publication.get() instead.", {});
    }
    this.#id = id;
    BaseSingletonEntity._callInitializeStore(this);
  }

  process(version, links, savingMessage = "All changes saved") {
    this.#store.$patch({
      currentVersion: version,
      links,
      designVariant: useConfigurationStore().designVariant,
      savingMessage,
      processed: true,
      hydrated: true,
    });

    return this;
  }

  /** ******************* Private Methods *********************/
  _initializeStore() {
    const storeKey = `publication-${this.#id}`;
    const store = this._defineStore(storeKey, () => ({
      currentVersion: 0,
      savingMessage: "All changes saved",
      designVariant: "desktop",
      links: {},
    }));
    this.#store = store;
  }
}

export default Publication;
